"use client";

import { useCreateMyBillMutation } from "@/hooks";
import { useToastStore } from "@/store/toastStore";
import { useSession } from "next-auth/react";
import { useState } from "react";
import { BdAddCircle, BdBell, BdBookmark } from "solar-icon-react/bd";

const BILL_LABEL_SUGGESTIONS = ["قبض خانه", "قبض مغازه", "قبض شرکت"];

type Props = {
  purchaseId: number;
  typeLabel: string;
  className?: string;
  onSaved?: () => void;
};

const SaveBillCard = ({
  purchaseId,
  typeLabel,
  className = "",
  onSaved,
}: Props) => {
  const [label, setLabel] = useState("");
  const [labelError, setLabelError] = useState("");
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [reminderDay, setReminderDay] = useState("15");
  const [reminderInterval, setReminderInterval] = useState<
    "monthly" | "bimonthly"
  >("monthly");
  const [isSaved, setIsSaved] = useState(false);
  const { data: session } = useSession();
  const { setOpenToast } = useToastStore();
  const { mutate: createMyBill, isPending } = useCreateMyBillMutation(
    session?.user.token,
  );

  const updateLabel = (value: string) => {
    setLabel(value);
    if (value.trim()) {
      setLabelError("");
    }
  };

  const saveBill = () => {
    const trimmedLabel = label.trim();

    if (!trimmedLabel) {
      const message = "نام قبض الزامی است.";
      setLabelError(message);
      setOpenToast({
        variant: "error",
        message,
      });
      return;
    }

    if (reminderEnabled && !reminderDay) {
      setOpenToast({
        variant: "error",
        message: "روز ارسال یادآور را مشخص کنید.",
      });
      return;
    }

    createMyBill(
      {
        purchase_id: purchaseId,
        label: trimmedLabel,
        reminder_enabled: reminderEnabled,
        ...(reminderEnabled && {
          reminder_day: Number(reminderDay),
          reminder_interval: reminderInterval,
        }),
      },
      {
        onSuccess: () => {
          setIsSaved(true);
          onSaved?.();
          setOpenToast({
            variant: "success",
            message: "قبض با موفقیت در قبض‌های من ذخیره شد.",
          });
        },
        onError: (error) =>
          setOpenToast({ variant: "error", message: error.message }),
      },
    );
  };

  return (
    <aside
      className={`border-primary-main/15 bg-primary-main/[0.035] mx-auto mt-8 w-full max-w-2xl rounded-3xl border p-5 sm:p-7 ${className}`}
    >
      <div className="flex items-start gap-3">
        <span className="bg-primary-main/10 text-primary-main flex size-10 shrink-0 items-center justify-center rounded-2xl">
          <BdBookmark size={23} />
        </span>
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            افزودن به قبض‌های من
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            این شناسه قبض را ذخیره کنید تا دفعه بعد بدون واردکردن مجدد شناسه،
            سریع‌تر استعلام و پرداخت کنید.
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto]">
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-gray-700">
            نام قبض <span className="text-error-main">*</span>
          </span>
          <input
            value={label}
            onChange={(event) => updateLabel(event.target.value)}
            placeholder={`مثلاً ${typeLabel} خانه`}
            required
            aria-invalid={Boolean(labelError)}
            aria-describedby={labelError ? "save-bill-label-error" : undefined}
            className={`h-12 w-full rounded-xl border bg-white px-4 text-sm transition outline-none focus:ring-4 ${
              labelError
                ? "border-error-main focus:border-error-main focus:ring-error-main/10"
                : "focus:border-primary-main focus:ring-primary-main/10 border-gray-200"
            }`}
          />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-400">پیشنهاد:</span>
            {BILL_LABEL_SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => updateLabel(suggestion)}
                className="hover:border-primary-main hover:text-primary-main rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600 transition"
              >
                {suggestion}
              </button>
            ))}
          </div>
          {labelError && (
            <p
              id="save-bill-label-error"
              className="text-error-main mt-2 text-xs font-medium"
            >
              {labelError}
            </p>
          )}
        </label>
        <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 sm:mt-7 sm:h-12">
          <input
            type="checkbox"
            checked={reminderEnabled}
            onChange={(event) => setReminderEnabled(event.target.checked)}
            className="accent-primary-main size-4"
          />
          <BdBell size={20} className="text-primary-main" />
          <span className="text-sm font-medium">یادآوری صدور قبض</span>
        </label>
      </div>

      {reminderEnabled && (
        <div className="mt-4 grid gap-3 rounded-2xl border border-gray-100 bg-white p-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">
              روز ارسال یادآور
            </span>
            <select
              value={reminderDay}
              onChange={(event) => setReminderDay(event.target.value)}
              className="focus:border-primary-main h-12 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm transition outline-none"
            >
              {Array.from({ length: 31 }, (_, index) => String(index + 1)).map(
                (day) => (
                  <option key={day} value={day}>
                    روز {day} هر ماه
                  </option>
                ),
              )}
            </select>
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">
              دوره ارسال
            </span>
            <select
              value={reminderInterval}
              onChange={(event) =>
                setReminderInterval(event.target.value as "monthly" | "bimonthly")
              }
              className="focus:border-primary-main h-12 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm transition outline-none"
            >
              <option value="monthly">هر ماه</option>
              <option value="bimonthly">هر دو ماه یک‌بار</option>
            </select>
          </label>
        </div>
      )}

      <button
        type="button"
        onClick={saveBill}
        disabled={isSaved || isPending}
        className="bg-primary-main hover:bg-primary-main/90 mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold text-white transition disabled:cursor-default disabled:opacity-70"
      >
        <BdAddCircle size={21} />
        {isSaved
          ? "در قبض‌های من ذخیره شد"
          : isPending
            ? "در حال ذخیره..."
            : "ذخیره قبض"}
      </button>
    </aside>
  );
};

export default SaveBillCard;
